<template>
  <div
    class="tw-flex tw-w-full tw-min-w-0 tw-items-center tw-text-sm tw-text-grey-medium"
    @click.stop
  >
    <Popover
      class="tw-w-full"
      fit-viewport
      :aria-label="`Delivery details: ${displayNumber(
        item.deliveredImpressions
      )} consolidated impressions; real-time: ${formattedRealtimeImpressions}`"
      button-class="tw-flex tw-w-full tw-items-center tw-justify-end tw-rounded tw-py-2 tw-text-right tw-tabular-nums"
      panel-class="tw-w-80 tw-max-w-[calc(100vw-2rem)] tw-p-4 tw-text-left tw-text-sm tw-font-normal tw-text-grey-medium"
    >
      <span class="tw-min-w-0">
        <span class="tw-block tw-truncate tw-text-sm tw-font-medium">
          {{ displayNumber(item.deliveredImpressions) }}
        </span>
        <span class="tw-block tw-truncate tw-text-xsm tw-text-grey-light">
          <svg
            v-if="hasRealtimeImpressions"
            aria-hidden="true"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="tw-inline-block tw-h-3 tw-w-3 tw-align-middle tw-text-primary"
          >
            <path d="m13 2-9 12h7l-1 8 10-12h-7l1-8Z" />
          </svg>
          {{ formattedRealtimeImpressions }}
        </span>
      </span>

      <template #popover>
        <h3 class="tw-font-semibold tw-text-blackest">Delivery details</h3>
        <p
          v-if="item.name"
          class="tw-mt-1 tw-break-words tw-font-medium tw-text-blackest"
        >
          {{ item.name }}
        </p>
        <p class="tw-mt-1 tw-text-xs">
          Consolidated impressions appear above real-time impressions in the
          table.
        </p>

        <section
          v-if="realtimeDelivery"
          class="tw-mt-4"
        >
          <h4 class="tw-mb-2 tw-font-medium tw-text-blackest">Real-time</h4>
          <dl class="tw-space-y-2">
            <div class="tw-flex tw-justify-between tw-gap-4">
              <dt>Impressions</dt>
              <dd class="tw-text-right tw-tabular-nums">
                {{ displayNumber(realtimeDelivery.deliveredImpressions) }}
              </dd>
            </div>
            <div
              v-if="hasNumber(realtimeDelivery.deliveredBudget)"
              class="tw-flex tw-justify-between tw-gap-4"
            >
              <dt>Delivered budget</dt>
              <dd class="tw-text-right tw-tabular-nums">
                {{ displayBudget(realtimeDelivery.deliveredBudget) }}
              </dd>
            </div>
          </dl>
          <p
            v-if="retrievedAt"
            class="tw-mt-3 tw-text-xs"
          >
            Retrieved
            <time
              :datetime="realtimeDelivery.retrievedAt"
              :title="retrievedAt"
            >
              {{ timeAgo(realtimeDelivery.retrievedAt) }}
            </time>
          </p>
        </section>
        <p
          v-else
          class="tw-mt-3 tw-text-xs"
        >
          Real-time delivery is unavailable.
        </p>

        <section class="tw-mt-4 tw-border-t tw-border-border tw-pt-3">
          <h4 class="tw-mb-2 tw-font-medium tw-text-blackest">Consolidated</h4>
          <dl>
            <div class="tw-flex tw-justify-between tw-gap-4">
              <dt>Impressions</dt>
              <dd class="tw-text-right tw-tabular-nums">
                {{ displayNumber(item.deliveredImpressions) }}
              </dd>
            </div>
          </dl>
          <p class="tw-mt-3 tw-text-xs">
            Consolidated figures may be delayed. Sorting and filters use
            consolidated impressions.
          </p>
        </section>
      </template>
    </Popover>
  </div>
</template>

<script>
import ListCellDefault from '@/components/list/cell/default.vue'
import Popover from '@/components/Popover.vue'
import { formatNumber, formatCurrency } from '@/helpers/number'
import { formatDateWithTime, timeAgo } from '@/helpers/date'

const hasNumber = (value) =>
  (typeof value === 'number' ||
    (typeof value === 'string' && value.trim() !== '')) &&
  Number.isFinite(Number(value))

export default {
  name: 'ListCellDelivery',
  extends: ListCellDefault,
  components: { Popover },
  emits: ['click'],
  computed: {
    realtimeDelivery() {
      return this.item.realtimeDelivery ?? null
    },
    hasRealtimeImpressions() {
      return hasNumber(this.realtimeDelivery?.deliveredImpressions)
    },
    formattedRealtimeImpressions() {
      return this.hasRealtimeImpressions
        ? this.displayNumber(this.realtimeDelivery.deliveredImpressions)
        : 'N/A'
    },
    retrievedAt() {
      const value = this.realtimeDelivery?.retrievedAt

      return value && Number.isFinite(new Date(value).getTime())
        ? formatDateWithTime(value)
        : null
    },
  },
  methods: {
    hasNumber,
    timeAgo,
    displayNumber(value) {
      return hasNumber(value) ? formatNumber(Number(value)) : '—'
    },
    displayBudget(value) {
      return this.item.currencyCode
        ? formatCurrency(Number(value), this.item.currencyCode)
        : formatNumber(Number(value), 2)
    },
  },
}
</script>
